import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { Currency } from "../../../types";
import type { ItemRow } from "./types";
import type { RequestFormValues } from "./requestFormSchema";

type GetValues = UseFormGetValues<RequestFormValues>;
type SetValue = UseFormSetValue<RequestFormValues>;

/**
 * The request-wide defaults handlers, factored out of `useNewPurchaseRequest` to keep each file
 * under budget. Each one sets the global value AND — while its "apply to all" toggle is on —
 * propagates the change into every existing item's RHF row.
 *
 * Propagation is **override-survives**: a change only overwrites items that are *still inherited*,
 * i.e. whose current value equals the OLD global value (read via `getValues` BEFORE `setValue`).
 * Items the requester has already overridden in the expanded editor keep their own value, so the
 * greyed-vs-normal "inherited from the request" rendering in the items table stays correct. Kept as
 * a plain factory over `getValues`/`setValue` (no hooks) so it stays a pure translation of those
 * handlers.
 */
export const createGlobalHandlers = (
  getValues: GetValues,
  setValue: SetValue
) => {
  const applyToItems = (mapItem: (item: ItemRow) => ItemRow) => {
    setValue("items", getValues("items").map(mapItem));
  };

  return {
    handleGlobalBudgetValueChange: (raw: string) => {
      const value = raw === "" ? null : Number(raw);
      const oldValue = getValues("globalBudgetValue");
      setValue("globalBudgetValue", value);
      if (getValues("useGlobalBudget")) {
        const currency = getValues("globalCurrency");
        applyToItems((item) =>
          // Only rows still carrying the old inherited amount follow the request.
          item.budgetValue === oldValue
            ? {
                ...item,
                currency,
                budgetCurrency: currency,
                budgetValue: value,
              }
            : item
        );
      }
    },

    handleGlobalCurrencyChange: (currency: Currency) => {
      const oldCurrency = getValues("globalCurrency");
      setValue("globalCurrency", currency);
      if (getValues("useGlobalBudget")) {
        applyToItems((item) =>
          item.currency === oldCurrency
            ? { ...item, currency, budgetCurrency: currency }
            : item
        );
      }
    },

    handleToggleGlobalBudget: (checked: boolean) => {
      setValue("useGlobalBudget", checked);
      if (checked) {
        const { globalCurrency, globalBudgetValue } = getValues();
        applyToItems((item) => ({
          ...item,
          currency: globalCurrency,
          budgetCurrency: globalCurrency,
          budgetValue: globalBudgetValue,
        }));
      }
    },

    handleGlobalNeededFromChange: (value: string) => {
      const oldFrom = getValues("globalNeededFrom") || null;
      const single = getValues("singleDateMode");
      const next = value || null;
      setValue("globalNeededFrom", value);
      if (single) setValue("globalNeededTo", value);
      if (getValues("useGlobalDates")) {
        applyToItems((item) => {
          if (single) {
            const inherited =
              item.neededFrom === oldFrom && item.neededTo === oldFrom;
            return inherited
              ? { ...item, neededFrom: next, neededTo: next }
              : item;
          }
          return item.neededFrom === oldFrom
            ? { ...item, neededFrom: next }
            : item;
        });
      }
    },

    handleGlobalNeededToChange: (value: string) => {
      const oldTo = getValues("globalNeededTo") || null;
      const single = getValues("singleDateMode");
      const next = value || null;
      setValue("globalNeededTo", value);
      if (single) setValue("globalNeededFrom", value);
      if (getValues("useGlobalDates")) {
        applyToItems((item) => {
          if (single) {
            const inherited =
              item.neededFrom === oldTo && item.neededTo === oldTo;
            return inherited
              ? { ...item, neededFrom: next, neededTo: next }
              : item;
          }
          return item.neededTo === oldTo ? { ...item, neededTo: next } : item;
        });
      }
    },

    handleToggleGlobalDates: (checked: boolean) => {
      setValue("useGlobalDates", checked);
      if (!checked) return;
      const { singleDateMode, globalNeededFrom, globalNeededTo } = getValues();
      applyToItems((item) => {
        if (singleDateMode) {
          const singleDate = globalNeededFrom || globalNeededTo;
          if (!singleDate) return item;
          return { ...item, neededFrom: singleDate, neededTo: singleDate };
        }
        return {
          ...item,
          neededFrom: globalNeededFrom || item.neededFrom,
          neededTo: globalNeededTo || item.neededTo,
        };
      });
    },

    setSingleDateMode: (single: boolean) => setValue("singleDateMode", single),
  };
};
